// A tiny, framework-free ring buffer of recent API calls, plus the
// openapi-fetch middleware that feeds it.
//
// Why it exists: the in-app feedback report (src/components/feedback/) only
// ever saw `console.error` lines (errorCollector.ts) — a failed mutation that
// is caught and turned into inline UI copy (the common case) never reaches
// `console.error`, so the report was blind to the request that actually
// failed. This module gives the report a timeline of what the client talked
// to the API about, independent of whether the call site logged anything.
//
// PRIVACY is the hard constraint here, not a nice-to-have: this buffer is
// attached verbatim to feedback reports, which humans read. It must never
// record:
//   - request bodies (may contain anything the user typed, including
//     passwords in a change-password form, comments, free text);
//   - response bodies of *successful* calls (same reasoning — only failure
//     bodies are parsed, and only for a few known-safe fields);
//   - query string values, except for allowlisted keys (ids, codes, enums,
//     paging; see traceQuery.ts). Search text is personal: other keys keep
//     their name and get `[redacted]`;
//   - any header other than `X-Request-ID` (no Authorization, no cookies).

import { sanitizeQuery } from './traceQuery'
import type { Middleware, MiddlewareCallbackParams } from 'openapi-fetch'

/** One row of the trace. `status: 0` means the request never got a response
 * (network error, aborted, CORS failure, …) — surfaced via openapi-fetch's
 * `onError`. */
export interface ApiTraceEntry {
  /** ISO 8601 timestamp of when the response (or error) was observed. */
  t: string
  method: string
  /** The OpenAPI schema path template, e.g. `/api/v1/organizations/{orgCode}/assets/{assetUid}` — never the resolved URL, so no ids leak through the path itself beyond what the template already names. */
  path: string
  /** `location.pathname` only — no origin, no query string. */
  url: string
  /** Sanitized query string (keys kept, non-allowlisted values redacted). Absent when there is none. */
  query?: string
  /** HTTP status code, or 0 for a network-level failure. */
  status: number
  duration_ms: number
  /** Parsed from the error body, capped at 200 chars. Only ever read for status >= 400. */
  code?: string
  title?: string
  /** From the `X-Request-ID` response header (or the error body's `request_id`, if present). */
  request_id?: string
  /** Set when this row collapses 2+ consecutive identical successful calls. */
  repeat?: number
}

const MAX_RECENT = 30
const MAX_FAILURES = 10
const MAX_MERGED = 40
const MAX_FIELD_LEN = 200

interface StoredEntry extends ApiTraceEntry {
  id: string
}

let recent: StoredEntry[] = []
let failures: StoredEntry[] = []

function isFailure(status: number): boolean {
  return status >= 400 || status === 0
}

function truncate(value: string): string {
  return value.length > MAX_FIELD_LEN ? value.slice(0, MAX_FIELD_LEN) : value
}

// A malformed or proxied response can repeat a header (Headers.get() joins
// repeated values with ", "), producing an unbounded string — cap it like
// any other field read off the wire, but keep `undefined` for "absent".
function truncateOptional(value: string | null): string | undefined {
  return value ? truncate(value) : undefined
}

function pushCapped(buffer: StoredEntry[], entry: StoredEntry, max: number): void {
  buffer.push(entry)
  while (buffer.length > max) buffer.shift()
}

/** Records one observed call. Never throws. */
function record(entry: StoredEntry): void {
  try {
    if (!isFailure(entry.status)) {
      const last = recent[recent.length - 1]
      if (last && !isFailure(last.status) && last.method === entry.method && last.path === entry.path && last.query === entry.query && last.status === entry.status) {
        last.t = entry.t
        last.duration_ms = entry.duration_ms
        last.repeat = (last.repeat ?? 1) + 1
        return
      }
      pushCapped(recent, entry, MAX_RECENT)
      return
    }

    // Failures get their own row in `recent` (no collapsing — each one may
    // carry a distinct code/request_id worth keeping) AND a copy in the
    // dedicated `failures` buffer, so 30 later successful polls cannot evict
    // the one failure that matters. `getApiTrace` de-dupes the two buffers
    // by `id`.
    pushCapped(recent, entry, MAX_RECENT)
    pushCapped(failures, entry, MAX_FAILURES)
  } catch {
    // A trace must never break the request it is observing.
  }
}

/** Merges `recent` + `failures`, de-duped by request id, sorted by time,
 * capped at 40. Never throws. */
export function getApiTrace(): ApiTraceEntry[] {
  try {
    const byId = new Map<string, StoredEntry>()
    for (const e of recent) byId.set(e.id, e)
    for (const e of failures) byId.set(e.id, e)
    const list = Array.from(byId.values()).sort((a, b) => a.t.localeCompare(b.t))
    const capped = list.slice(-MAX_MERGED)
    return capped.map(({ id: _id, ...rest }) => rest)
  } catch {
    return []
  }
}

/** Test-only: clears both buffers so tests don't leak state into each other. */
export function resetApiTraceForTests(): void {
  recent = []
  failures = []
}

/** Pure summary of a trace for display: `null` when there is nothing to show
 * (empty trace — the caller should hide its suffix entirely), otherwise the
 * total count and how many of those failed. Reuses `isFailure` so the
 * "what counts as a failure" rule lives in exactly one place. */
export function apiTraceSummary(trace: ApiTraceEntry[]): { count: number; failed: number } | null {
  if (trace.length === 0) return null
  const failed = trace.filter((entry) => isFailure(entry.status)).length
  return { count: trace.length, failed }
}

// --- openapi-fetch middleware -----------------------------------------

interface PendingCall {
  startedAt: number
  method: string
  path: string
  url: string
  query?: string
}

const pending = new Map<string, PendingCall>()
const MAX_PENDING = 100

function pendingKey(id: string): string {
  return id
}

function rememberPending(id: string, call: PendingCall): void {
  pending.set(pendingKey(id), call)
  // Belt-and-braces: a request whose response/error callback never fires
  // (should not happen, but this is a diagnostics path — it must never grow
  // unbounded) gets evicted oldest-first once we have too many in flight.
  if (pending.size > MAX_PENDING) {
    const oldestKey = pending.keys().next().value
    if (oldestKey !== undefined) pending.delete(oldestKey)
  }
}

function takePending(id: string): PendingCall | undefined {
  const key = pendingKey(id)
  const call = pending.get(key)
  pending.delete(key)
  return call
}

/**
 * The api-trace middleware. Registered at module scope in `api/client.ts`
 * next to the 403-notification middleware — same contract: never throws,
 * always returns its input untouched, never ejected.
 */
export const apiTraceMiddleware: Middleware = {
  onRequest({ request, schemaPath, id }: MiddlewareCallbackParams) {
    try {
      const url = new URL(request.url)
      rememberPending(id, {
        startedAt: typeof performance !== 'undefined' ? performance.now() : Date.now(),
        method: request.method,
        path: schemaPath,
        url: url.pathname,
        query: sanitizeQuery(url.search),
      })
    } catch {
      // Never block the request over a tracing failure.
    }
    return undefined
  },

  async onResponse({ response, id }: MiddlewareCallbackParams & { response: Response }) {
    try {
      const call = takePending(id)
      if (!call) return response

      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const entry: StoredEntry = {
        id,
        t: new Date().toISOString(),
        method: call.method,
        path: call.path,
        url: call.url,
        ...(call.query ? { query: call.query } : {}),
        status: response.status,
        duration_ms: Math.round(now - call.startedAt),
        request_id: truncateOptional(response.headers.get('X-Request-ID')),
      }

      if (response.status >= 400) {
        try {
          // Clone: the caller still needs to read the body itself.
          const body = await response.clone().json()
          if (body && typeof body === 'object') {
            if (typeof body.code === 'string') entry.code = truncate(body.code)
            if (typeof body.title === 'string') entry.title = truncate(body.title)
            if (typeof body.request_id === 'string') entry.request_id = truncate(body.request_id)
          }
        } catch {
          // Not JSON, or already consumed — the status/duration/path alone
          // are still useful, so record the entry without the body fields.
        }
      }

      record(entry)
    } catch {
      // Never turn a tracing failure into a request failure.
    }
    return response
  },

  onError({ error, id }: MiddlewareCallbackParams & { error: unknown }) {
    try {
      const call = takePending(id)
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      record({
        id,
        t: new Date().toISOString(),
        method: call?.method ?? 'GET',
        path: call?.path ?? '',
        url: call?.url ?? '',
        ...(call?.query ? { query: call.query } : {}),
        status: 0,
        duration_ms: call ? Math.round(now - call.startedAt) : 0,
        title: truncate(String(error)),
      })
    } catch {
      // Never turn a tracing failure into a thrown error.
    }
    return undefined
  },
}
